import React, { useState, useMemo, useRef, useEffect } from 'react';
import InputStyle from '../style.ts';
import Style from './style.module.css';

const normalizeOption = (opt) =>
  typeof opt === 'string' ? { value: opt, label: opt } : opt;

const SearchableDropdown = ({
  id,
  label,
  options,
  value,
  onChange,
  placeholder = 'Search...',
  optionalNote = '',
  multiple = false,
  selectOnly = false,
  showAllOption = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef(null);

  const normalizedOptions = useMemo(
    () => options.map(normalizeOption),
    [options],
  );

  const labelByValue = useMemo(() => {
    const map = new Map();
    normalizedOptions.forEach((opt) => map.set(opt.value, opt.label));
    return map;
  }, [normalizedOptions]);

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return normalizedOptions;
    const query = search.toLowerCase();
    return normalizedOptions.filter(
      (opt) =>
        opt.label.toLowerCase().includes(query) ||
        opt.value.toLowerCase().includes(query),
    );
  }, [normalizedOptions, search]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedValues = useMemo(() => {
    if (!multiple) {
      return [];
    }
    if (Array.isArray(value)) {
      return value;
    }
    if (typeof value === 'string' && value.trim()) {
      return value
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean);
    }
    return [];
  }, [multiple, value]);

  const displayValue = useMemo(() => {
    if (multiple) {
      return selectedValues.map((v) => labelByValue.get(v) || v).join(', ');
    }
    return labelByValue.get(value) || value || '';
  }, [multiple, selectedValues, value, labelByValue]);

  const handleFocus = () => {
    setSearch('');
    setIsOpen(true);
  };

  const handleInputChange = (e) => {
    const next = e.target.value;
    setSearch(next);
    if (!multiple && !selectOnly) {
      onChange({ target: { id, value: next } });
    }
  };

  const handleSelect = (selectedValue) => {
    if (!multiple) {
      onChange({ target: { id, value: selectedValue } });
      setIsOpen(false);
      setSearch('');
      return;
    }

    let nextValues;
    if (selectedValue === 'All') {
      nextValues = ['All'];
    } else {
      const withoutAll = selectedValues.filter((v) => v !== 'All');
      nextValues = withoutAll.includes(selectedValue)
        ? withoutAll.filter((v) => v !== selectedValue)
        : [...withoutAll, selectedValue];
    }

    onChange({ target: { id, value: nextValues.join(',') } });
    setSearch('');
  };

  const handleOptionMouseDown = (e, selectedValue) => {
    if (multiple) {
      e.preventDefault();
    }
    handleSelect(selectedValue);
  };

  const handleClear = () => {
    onChange({ target: { id, value: '' } });
    setIsOpen(false);
    setSearch('');
  };

  const handleBlur = () => {
    setIsOpen(false);
    setSearch('');
  };

  return (
    <span
      style={{ ...InputStyle.container, position: 'relative' }}
      ref={wrapperRef}
    >
      <label style={InputStyle.label(true)} htmlFor={id}>
        {label}
        {optionalNote && (
          <span className={Style.optionalNote}> {optionalNote}</span>
        )}
      </label>
      <input
        className={Style.inputText}
        id={id}
        type="text"
        value={
          isOpen ? (multiple ? search || displayValue : search) : displayValue
        }
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        autoComplete="off"
      />
      {isOpen && (
        <ul className={Style.dropdownList}>
          <li
            onMouseDown={handleClear}
            className={`${Style.dropdownItem} ${!value ? Style.selected : ''} ${Style.clearOption}`}
          >
            — Clear selection —
          </li>

          {showAllOption && (
            <li
              onMouseDown={(e) => handleOptionMouseDown(e, 'All')}
              className={`${Style.dropdownItem} ${multiple ? (selectedValues.includes('All') ? Style.selected : '') : value === 'All' ? Style.selected : ''}`}
            >
              All
            </li>
          )}

          {filteredOptions.map((opt) => (
            <li
              key={opt.value}
              onMouseDown={(e) => handleOptionMouseDown(e, opt.value)}
              className={`${Style.dropdownItem} ${multiple ? (selectedValues.includes(opt.value) ? Style.selected : '') : value === opt.value ? Style.selected : ''}`}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </span>
  );
};

export default SearchableDropdown;
